import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle, XCircle, Loader2, Eye, Download, FileText, Image, MessageSquare, Send } from "lucide-react";
import { getVerificationStatusColor, getDocStatusColor } from "./utils";
import api from "@/lib/api";
import { toast } from "sonner";

export function DocumentReviewDialog({
  open,
  onOpenChange,
  selectedPartner,
  documents,
  processingDoc,
  onApprove,
  onReject,
  onRefresh
}) {
  const [viewingDoc, setViewingDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [adminComment, setAdminComment] = useState("");
  const [sendingComment, setSendingComment] = useState(false);

  const viewDocument = async (doc) => {
    setLoadingDoc(true);
    try {
      // Fetch full document with file_data
      const response = await api.get(`/partner-verification/admin/document/${doc.id}`);
      setViewingDoc(response.data);
    } catch (error) {
      toast.error("Failed to load document");
    } finally {
      setLoadingDoc(false);
    }
  };

  const sendComment = async (docId) => {
    if (!adminComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }
    setSendingComment(true);
    try {
      await api.post(`/partner-verification/admin/comment/${docId}`, {
        comment: adminComment
      });
      toast.success("Comment sent to partner");
      setAdminComment("");
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error("Failed to send comment");
    } finally {
      setSendingComment(false);
    }
  };

  const isImageFile = (fileName) => {
    const ext = fileName?.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
  };

  const isPdfFile = (fileName) => {
    return fileName?.toLowerCase().endsWith('.pdf');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Partner Documents Review</DialogTitle>
        </DialogHeader>
        
        {selectedPartner && (
          <div className="space-y-4">
            <div className="border-b border-border pb-4">
              <p className="font-bold text-lg">{selectedPartner.company_name || selectedPartner.name}</p>
              <p className="text-sm text-muted-foreground">{selectedPartner.email}</p>
              {selectedPartner.phone && (
                <p className="text-sm text-muted-foreground">Phone: {selectedPartner.phone}</p>
              )}
              <span className={`inline-block mt-2 px-3 py-1 text-xs font-bold uppercase ${getVerificationStatusColor(selectedPartner.verification_status || 'not_started')}`}>
                {selectedPartner.verification_status || 'Not Started'}
              </span>
            </div>
            
            {/* Document Viewer */}
            {viewingDoc && (
              <div className="border border-border p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold">{viewingDoc.document_name}</h3>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setViewingDoc(null)}
                    className="rounded-none"
                  >
                    Close Preview
                  </Button>
                </div>
                
                {viewingDoc.file_data && (
                  <div className="bg-white border border-border p-2 mb-3">
                    {isImageFile(viewingDoc.file_name) ? (
                      <img 
                        src={viewingDoc.file_data.startsWith('data:') 
                          ? viewingDoc.file_data 
                          : `data:image/${viewingDoc.file_name.split('.').pop()};base64,${viewingDoc.file_data}`
                        }
                        alt={viewingDoc.document_name}
                        className="max-w-full max-h-[400px] mx-auto object-contain"
                      />
                    ) : isPdfFile(viewingDoc.file_name) ? (
                      <div className="text-center py-8">
                        <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground mb-3">PDF Document: {viewingDoc.file_name}</p>
                        <a 
                          href={viewingDoc.file_data.startsWith('data:') 
                            ? viewingDoc.file_data 
                            : `data:application/pdf;base64,${viewingDoc.file_data}`
                          }
                          download={viewingDoc.file_name}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-white hover:bg-accent/90"
                        >
                          <Download className="w-4 h-4" />
                          Download PDF
                        </a>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground mb-3">{viewingDoc.file_name}</p>
                        <a 
                          href={viewingDoc.file_data}
                          download={viewingDoc.file_name}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-white hover:bg-accent/90"
                        >
                          <Download className="w-4 h-4" />
                          Download File
                        </a>
                      </div>
                    )}
                  </div>
                )}

                {/* Admin Comment Section */}
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Send Comment to Partner (Request Re-upload)
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={adminComment}
                      onChange={(e) => setAdminComment(e.target.value)}
                      placeholder="e.g., Document is blurry, please upload a clearer image..."
                      className="rounded-none flex-1"
                    />
                    <Button
                      onClick={() => sendComment(viewingDoc.id)}
                      disabled={sendingComment || !adminComment.trim()}
                      className="rounded-none"
                    >
                      {sendingComment ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-1" />
                          Send
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Partner will receive a notification with your comment
                  </p>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              {documents.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No documents uploaded</p>
              ) : (
                documents.map((doc) => (
                  <DocumentCard
                    key={doc.id}
                    doc={doc}
                    processing={processingDoc === doc.id}
                    loadingDoc={loadingDoc}
                    onApprove={onApprove}
                    onReject={onReject}
                    onView={() => viewDocument(doc)}
                    isViewing={viewingDoc?.id === doc.id}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DocumentCard({ doc, processing, loadingDoc, onApprove, onReject, onView, isViewing }) {
  return (
    <div className={`border p-4 ${isViewing ? 'border-accent bg-accent/5' : 'border-border'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {doc.file_name?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) ? (
              <Image className="w-4 h-4 text-muted-foreground" />
            ) : (
              <FileText className="w-4 h-4 text-muted-foreground" />
            )}
            <p className="font-medium">{doc.document_name}</p>
          </div>
          <p className="text-sm text-muted-foreground">{doc.file_name}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()}
          </p>
          <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-bold uppercase ${getDocStatusColor(doc.status)}`}>
            {doc.status}
          </span>
          {doc.rejection_reason && (
            <p className="text-sm text-red-600 mt-1">Rejection: {doc.rejection_reason}</p>
          )}
          {doc.admin_comment && (
            <p className="text-sm text-blue-600 mt-1">Comment: {doc.admin_comment}</p>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {/* View Document Button */}
          <Button
            size="sm"
            variant="outline"
            onClick={onView}
            disabled={loadingDoc}
            className="rounded-none"
            data-testid={`view-doc-${doc.id}`}
          >
            {loadingDoc && isViewing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Eye className="w-4 h-4 mr-1" />
                View
              </>
            )}
          </Button>
          
          {doc.status === 'pending' && (
            <>
              <Button
                size="sm"
                onClick={() => onApprove(doc.id)}
                disabled={processing}
                className="rounded-none bg-green-600 hover:bg-green-700"
                data-testid={`approve-doc-${doc.id}`}
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Approve
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onReject(doc.id)}
                disabled={processing}
                className="rounded-none"
                data-testid={`reject-doc-${doc.id}`}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function RejectDocumentDialog({
  open,
  onOpenChange,
  rejectReason,
  onReasonChange,
  onConfirm,
  processing
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reject Document</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Rejection Reason</label>
            <Input
              value={rejectReason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="rounded-none mt-1"
              data-testid="reject-reason-input"
            />
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 rounded-none"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirm}
              disabled={processing}
              className="flex-1 rounded-none"
              data-testid="confirm-reject-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reject Document"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
